import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Api from 'services/api';
import ToastApi from 'services/toast';
import { useProgressStatus } from 'hooks/ApiStatus';

import { Col, Row } from 'components/grid';
import FileDropZone from 'components/fileDropZone';
import PageTitleAndDescription from 'components/pageTitleAndDescription';
import ImporterProgressBar from './components/importerProgressBar';

import CTAs from './components/CTAs';
import { FileNameInfo, PageWrapper } from './styles';

const ImporterPage = () => {
  const { t } = useTranslation();

  const progressStatus = useProgressStatus();

  const [isApiRunning, setIsApiRunning] = useState(false);
  const [isProcessingComplete, setIsProcessingComplete] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [fileInfo, setFileInfo] = useState();

  const clean = () => {
    setProcessingProgress(0);
    setUploadProgress(0);
    setIsApiRunning(false);
    setFileInfo(null);
    setIsProcessingComplete(false);
  };

  const handleApiProcessProgress = (event) => {
    const { status, progress, message } = event;
    if (status === 'PROGRESS') {
      setProcessingProgress(progress);
    } else if (status === 'COMPLETED') {
      ToastApi.success(t('pages.importerPage.successMessage'));
      setIsProcessingComplete(true);
    } else if (status === 'ERROR') {
      ToastApi.error(`${t('error')}: ${message}`);
      clean();
    }
  };

  useEffect(() => {
    const asyncInitFunction = async () => {
      try {
        const { isRunning, fileName } = await Api.status();

        if (isRunning) {
          setIsApiRunning(true);
          setUploadProgress(100);
          setFileInfo({ name: fileName });
        }
      } catch (error) {
        ToastApi.error(error.message);
      }
    };
    asyncInitFunction();
  }, []);

  useEffect(() => {
    handleApiProcessProgress(progressStatus);
  }, [progressStatus]);

  const handleChangeFile = (file) => {
    if (file) {
      setFileInfo(file);
      setIsApiRunning(true);
      Api.uploadImporterFile(file, setUploadProgress);
    }
  };

  const handleOnClickCancel = () => {
    Api.cancelProcessement();
    clean();
  };

  const renderProgressAndCTAs = () => (
    <Col>
      <ImporterProgressBar label={t('pages.importerPage.uploadProgressBarLabel')} progress={uploadProgress} />
      <ImporterProgressBar label={t('pages.importerPage.processingProgressBarLabel')} progress={processingProgress} />
      <Row justifyContent="end">
        <CTAs
          handleOnClickCancel={handleOnClickCancel}
          clean={clean}
          uploadProgress={uploadProgress}
          isProcessingComplete={isProcessingComplete}
        />
      </Row>
    </Col>
  );

  const renderFileInput = () => (
    <Col>
      <Row justifyContent="center">
        <Col col={10}>
          <FileDropZone
            handleChangeFile={handleChangeFile}
            onDropRejected={() => ToastApi.error(t('pages.importerPage.invalidFileFormat'))}
          />
        </Col>
      </Row>
    </Col>
  );

  const renderFileInfo = () => <FileNameInfo>{fileInfo.name}</FileNameInfo>;

  return (
    <PageWrapper>
      <PageTitleAndDescription
        title={t('pages.importerPage.title')}
        description={t('pages.importerPage.description')}
      />
      {(fileInfo && isApiRunning) && renderFileInfo()}
      {isApiRunning ? renderProgressAndCTAs() : renderFileInput()}
    </PageWrapper>
  );
};

export default ImporterPage;
